import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";
import {
  getActivationCtaLabel,
  getActivationSupportCopy,
  getAssignedPlan,
  getPlanLabel,
  getRoleLabel,
  getRoleFeatures,
  getStatusBadgeClass,
  getStatusLabel,
  getTrialDaysCopy,
} from "../../constants/plans";
import { getActivationJourneySteps } from "../../constants/activationJourney";
import { getResumeActivationCta } from "../../constants/activationResume";

export const SubscriptionSection = ({
  currentUser,
  subscriptionInfo,
  entitlements,
  paymentStatus,
  canActivateSubscription,
  isActivatingSubscription,
  handleActivateSubscription,
  handleManageBilling,
  navigate,
  subscriptionTabOpen,
}) => {
  const assignedPlan = getAssignedPlan(currentUser);
  const roleFeatures = getRoleFeatures(currentUser?.role);
  const trialEndsAt =
    subscriptionInfo?.trial_ends_at || currentUser?.trial_ends_at;
  const isTrialing = paymentStatus === "trialing";
  const hasUsedTrial = !!currentUser?.trial_used;
  const activationCtaLabel = getActivationCtaLabel(currentUser);
  const activationSupportCopy = getActivationSupportCopy(currentUser);
  const activationJourneySteps = getActivationJourneySteps({
    planLabel: getPlanLabel(assignedPlan, "price"),
    trialDaysCopy: getTrialDaysCopy(),
    hasUsedTrial,
  });
  const resumeActivationCta = getResumeActivationCta({
    isAuthenticated: true,
  });

  const trialDaysLeft = (() => {
    if (!trialEndsAt) return null;
    const trialDate = new Date(trialEndsAt);
    if (Number.isNaN(trialDate.getTime())) return null;
    const diffMs = trialDate.getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return days < 0 ? 0 : days;
  })();

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-[1.35fr_0.95fr] gap-6 mb-6 ${subscriptionTabOpen ? "scroll-mt-24" : ""}`}
    >
      <div className="bg-background rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Cuenta y activación
            </p>
            <h2
              className="text-2xl font-bold"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Resumen de cuenta, plan y activación
            </h2>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
              paymentStatus,
            )}`}
          >
            {getStatusLabel(paymentStatus)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="rounded-xl border border-border p-4 bg-muted/20">
            <p className="text-xs text-muted-foreground mb-1">Tipo de cuenta</p>
            <p className="font-semibold">{getRoleLabel(currentUser?.role)}</p>
          </div>
          <div
            className="rounded-xl border border-border p-4 bg-muted/20"
            data-testid="account-plan-display"
          >
            <p className="text-xs text-muted-foreground mb-1">Plan asignado</p>
            <p className="font-semibold">
              {getPlanLabel(assignedPlan, "price")}
            </p>
          </div>
          <div className="rounded-xl border border-border p-4 bg-muted/20">
            <p className="text-xs text-muted-foreground mb-1">Estado</p>
            <p className="font-semibold">{getStatusLabel(paymentStatus)}</p>
          </div>
        </div>

        {canActivateSubscription ? (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">
                  Tu plan premium aún no está activado
                </h3>
                <p className="text-sm text-blue-800 mt-1">
                  Ya tienes asignado {getPlanLabel(assignedPlan, "price")}. {` ${activationSupportCopy.replace(/\.$/, "")}`} para habilitar las funciones operativas de {getRoleLabel(currentUser?.role)}.
                </p>
                {hasUsedTrial && (
                  <p className="text-xs text-blue-800/90 mt-2">
                    Esta cuenta ya utilizó su período de prueba y no puede
                    volver a usarlo.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  {activationJourneySteps.map((step) => (
                    <div
                      key={step.key}
                      className="rounded-lg border border-blue-200/80 bg-white/70 px-3 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-[0.18em] text-blue-700/80 mb-1">{step.eyebrow}</p>
                      <p className="text-sm font-medium text-blue-950">{step.title}</p>
                      <p className="text-xs text-blue-900/80 mt-1">{step.description}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    onClick={handleActivateSubscription}
                    disabled={isActivatingSubscription}
                    data-testid="activate-subscription-button"
                  >
                    {isActivatingSubscription
                      ? "Redirigiendo..."
                      : activationCtaLabel}
                  </Button>
                  {entitlements?.can_manage_billing && (
                    <Button variant="outline" onClick={handleManageBilling}>
                      Gestionar facturación
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                  >
                    Volver al Dashboard
                  </Button>
                </div>
                <div className="rounded-lg border border-blue-200/70 bg-white/60 px-3 py-3 mt-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-blue-700/80 mb-1">
                    {resumeActivationCta.eyebrow}
                  </p>
                  <p className="text-sm font-medium text-blue-950">
                    {resumeActivationCta.title}
                  </p>
                  <p className="text-xs text-blue-900/80 mt-1">
                    {resumeActivationCta.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`rounded-xl border p-4 mb-4 ${
              isTrialing
                ? "bg-blue-50 border-blue-200"
                : assignedPlan === "free"
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
            }`}
          >
            <div className="flex items-start gap-3">
              <CheckCircle2
                className={`w-5 h-5 mt-0.5 ${
                  isTrialing
                    ? "text-blue-600"
                    : assignedPlan === "free"
                      ? "text-green-600"
                      : "text-amber-600"
                }`}
              />
              <div>
                <h3 className="font-semibold">
                  {isTrialing
                    ? "Tu prueba gratuita está activa"
                    : assignedPlan === "free"
                      ? "Tu cuenta gratuita está activa"
                      : "Tu suscripción está activa"}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isTrialing
                    ? `Estás usando tu prueba de ${getTrialDaysCopy()} de ${getPlanLabel(assignedPlan, "price")}.`
                    : assignedPlan === "free"
                      ? "Puedes explorar propiedades, desarrollos, guardar favoritos y publicar 1 propiedad sin costo."
                      : "Tus pagos se gestionan vía Stripe y ya tienes acceso a los límites, herramientas y funciones premium de tu rol."}
                </p>
                {isTrialing && trialDaysLeft !== null && (
                  <p className="text-sm font-medium text-blue-700 mt-2">
                    {trialDaysLeft > 0
                      ? `Te quedan ${trialDaysLeft} día${trialDaysLeft === 1 ? "" : "s"} de prueba.`
                      : "Tu período de prueba termina hoy."}
                  </p>
                )}
                {isTrialing && (
                  <div className="mt-3 inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-xs font-medium">
                    Facturación automática al terminar {getTrialDaysCopy()} salvo cancelación previa en Stripe.
                  </div>
                )}
                {entitlements?.can_manage_billing && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={handleManageBilling}
                  >
                    {isTrialing
                      ? "Gestionar método de pago en Stripe"
                      : "Gestionar facturación en Stripe"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {subscriptionInfo?.renewal_date &&
          ["active", "trialing"].includes(paymentStatus) &&
          assignedPlan !== "free" && (
            <div className="text-sm text-muted-foreground">
              {isTrialing ? "La facturación inicia:" : "Próxima renovación:"}{" "}
              <span className="font-medium text-foreground">
                {subscriptionInfo.renewal_date}
              </span>
            </div>
          )}
      </div>

      <div className="bg-background rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Qué incluye tu cuenta</h2>
        </div>
        <div className="space-y-3 mb-6">
          {roleFeatures.map((feature) => (
            <div
              key={feature}
              className="rounded-lg border border-border bg-muted/20 px-3 py-3 text-sm"
            >
              {feature}
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-muted/30 border border-border p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-2">Regla del producto</p>
          <p>
            Tu rol define automáticamente tu plan. Si tu suscripción se pausa,
            tu información permanece disponible y puedes reactivar cuando lo necesites.
          </p>
        </div>
      </div>
    </div>
  );
};
